import { pb } from './pocketbase';

/**
 * Service pour gérer les demandes de contact
 */
class ContactService {
  constructor() {
    this.collection = 'contact_requests';
  }

  /**
   * Soumet une demande de contact
   * @param {Object} contactData - Données du formulaire de contact
   * @param {string} contactData.type - Type de demande (assistance|demo|rdv)
   * @param {string} contactData.nom - Nom du contact
   * @param {string} contactData.email - Email du contact
   * @param {string} contactData.societe - Société (optionnel)
   * @param {string} contactData.message - Message
   * @returns {Promise<Object>} Réponse de PocketBase
   */
  async submitContactRequest(contactData) {
    try {
      // Validation côté client
      this.validateContactData(contactData);

      // Vérification honeypot côté client
      if (contactData.website && contactData.website.trim() !== '') {
        throw new Error('Requête suspecte détectée');
      }

      // Préparation des données pour PocketBase
      const data = {
        type: contactData.type,
        nom: contactData.nom.trim(),
        email: contactData.email.trim().toLowerCase(),
        societe: contactData.societe ? contactData.societe.trim() : '',
        message: contactData.message.trim(),
        status: 'pending',
        ip_address: await this.getClientIP(),
        user_agent: navigator.userAgent
      };

      const record = await pb.collection(this.collection).create(data);
      
      return {
        success: true,
        data: record,
        message: 'Votre demande a été envoyée avec succès. Nous vous répondrons dans les plus brefs délais.'
      };
    } catch (error) {
      console.error('Erreur lors de l\'envoi de la demande de contact:', error);
      
      // Gestion des erreurs PocketBase
      if (error.response && error.response.data) {
        const pbError = error.response.data;
        if (pbError.code === 400) {
          return {
            success: false,
            error: 'Données invalides',
            message: 'Veuillez vérifier les informations saisies.'
          };
        }
      }
      
      return {
        success: false,
        error: error.message || 'Une erreur est survenue lors de l\'envoi de votre demande.',
        message: 'Erreur lors de l\'envoi. Veuillez réessayer ou nous contacter directement à contact@edutio.fr.'
      };
    }
  }

  /**
   * Valide les données du formulaire de contact
   * @param {Object} contactData - Données à valider
   */
  validateContactData(contactData) {
    const errors = [];

    // Validation du type
    if (!contactData.type || !['assistance', 'demo', 'rdv'].includes(contactData.type)) {
      errors.push('Type de demande invalide');
    }

    // Validation du nom
    if (!contactData.nom || contactData.nom.trim().length < 2) {
      errors.push('Le nom doit contenir au moins 2 caractères');
    }

    // Validation de l'email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!contactData.email || !emailRegex.test(contactData.email)) {
      errors.push('Adresse email invalide');
    }

    // Validation du message
    if (!contactData.message || contactData.message.trim().length < 10) {
      errors.push('Le message doit contenir au moins 10 caractères');
    }

    if (errors.length > 0) {
      throw new Error(errors.join(', '));
    }
  }

  /**
   * Récupère l'adresse IP du client (approximative)
   * @returns {Promise<string>} Adresse IP
   */
  async getClientIP() {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch (error) {
      return 'unknown';
    }
  }

  /**
   * Récupère toutes les demandes de contact (admin uniquement)
   * @param {Object} options - Options de récupération
   * @returns {Promise<Object>} Liste des demandes
   */
  async getContactRequests(options = {}) {
    try {
      const {
        page = 1,
        perPage = 20,
        sort = '-created',
        filter = ''
      } = options;

      const records = await pb.collection(this.collection).getList(page, perPage, {
        sort,
        filter
      });

      return {
        success: true,
        data: records
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des demandes:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Marque une demande comme traitée
   * @param {string} id - ID de la demande
   * @returns {Promise<Object>} Résultat de la mise à jour
   */
  async markAsProcessed(id) {
    try {
      const record = await pb.collection(this.collection).update(id, {
        status: 'processed',
        processed_at: new Date().toISOString()
      });

      return {
        success: true,
        data: record
      };
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
}

export default new ContactService();
